import React from "react";
import { createPortal } from "react-dom";
import { format, parseISO } from "date-fns";

const fmtDate = (v) => {
  if (!v) return "—";
  const d = typeof v === "string" ? parseISO(v) : new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : format(d, "d MMM yyyy");
};

const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = typeof v === "string" ? parseISO(v) : new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : format(d, "d MMM yyyy, HH:mm");
};

const fmtNum = (v, decimals = 3) => {
  if (v === undefined || v === null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return String(Number(n.toFixed(decimals)));
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-sm font-semibold text-gray-700 mb-2 border-b border-gray-100 pb-1">
      {title}
    </h3>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

const ProductDetailsModal = ({ open, onClose, loading, error, data, onRetry }) => {
  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const p = data || {};
  const titleSku = p.sku || p.skuName || p._id || "—";

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-3 md:p-4 font-nunito"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-details-title"
    >
      <div className="absolute inset-0 bg-black/20" onClick={handleBackdropClick} aria-hidden="true" />
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-[18px] border border-[#EEF1F5] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between shrink-0 px-6 pt-6 pb-2">
          <h2 id="product-details-title" className="text-lg font-semibold text-[#101828]">
            {loading ? "Product Details" : `Product Details — ${titleSku}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full border border-[#D0D5DD] text-[#667085] flex items-center justify-center bg-white hover:bg-[#F6F7FA] transition"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <hr className="border-0 border-t border-[#EEF1F5] mt-2" />

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <div className="py-8 text-center text-gray-500 text-sm">Loading...</div>}

          {error && !loading && (
            <div className="py-8 text-center">
              <p className="text-red-600 text-sm mb-3">{error}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded-xl border border-[#188742] bg-[#188742]/10 px-4 py-2 text-sm font-medium text-[#188742] hover:bg-[#188742]/20"
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {!loading && !error && data && (
            <>
              <Section title="Basic">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">SKU:</span> {p.sku ?? "—"}</div>
                  <div><span className="text-gray-500">SKU Name:</span> {p.skuName ?? "—"}</div>
                  <div><span className="text-gray-500">Status:</span> {p.status ?? "—"}</div>
                  <div><span className="text-gray-500">Outer UOM:</span> {p.uom ?? "—"}</div>
                  <div><span className="text-gray-500">Gross Weight(per Pallet UOM):</span> {fmtNum(p.grossWeightAsPerPalletUom ?? p.grossWeight ?? p.weight, 3)}</div>
                  <div>
  <span className="text-gray-500">Weight Per Pallet (kg):</span> 
  {fmtNum(p.weightPerPallet, 3)}
</div>
                  <div><span className="text-gray-500">Batch Controlled:</span> {p.isBatchControlled ? "Yes" : "No"}</div>
                </div>
                <div className="mt-2">
                  <span className="text-gray-500">Company: </span>
                  {(p.companies && p.companies.length > 0)
                    ? p.companies.map((c) => `${c.name || "—"} (${c.companyCode ?? "—"})`).join(", ")
                    : "—"}
                </div>
                {/* <div className="mt-2">
                  <span className="text-gray-500">Description: </span>
                  <span className="text-[#101828]">{p.description ?? "—"}</span>
                </div> */}
              </Section>

              <Section title="Packaging / Pallet">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">Base Price:</span> {fmtNum(p.basePrice, 2)}</div>
                  <div><span className="text-gray-500">Pallet:</span> {p.casesPerPallet != null ? p.casesPerPallet : "—"}</div>
                  {/* <div><span className="text-gray-500">Inner UOM:</span> {p.innerUomConfig?.uom ?? "—"}</div> */}

                  {/* <div><span className="text-gray-500">Inner UOM Qty:</span> {fmtNum(p.innerUomQty, 3)}</div>
                  <div><span className="text-gray-500">Pallet UOM:</span> {p.palletUom ?? "—"}</div> */}

                  <div><span className="text-gray-500">Conversion/Pallet:</span> {fmtNum(p.conversionQtyPerPallet, 3)}</div>
                  <div><span className="text-gray-500">Qty In Pallet:</span> {fmtNum(p.qtyInPallet, 3)}</div>
                </div>
              </Section>

              <Section title="Dimensions (cm)">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">Length:</span> {p.dimensions?.length ?? "—"}</div>
                  <div><span className="text-gray-500">Breadth:</span> {p.dimensions?.breadth ?? "—"}</div>
                  <div><span className="text-gray-500">Height:</span> {p.dimensions?.height ?? "—"}</div>
                </div>
              </Section>

              <Section title="Meta">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">Supplier:</span> {p.supplierName ?? "—"}</div>
                  <div><span className="text-gray-500">SKU Category:</span> {p.skuCategory ?? "—"}</div>
                  <div><span className="text-gray-500">Storage Type:</span> {p.storageType ?? "—"}</div>
<div><span className="text-gray-500">Product Type:</span> {p.productType ?? "—"}</div>
                  {/* <div><span className="text-gray-500">MFG Date:</span> {fmtDate(p.manufacturingDate)}</div>
                  <div><span className="text-gray-500">EXP Date:</span> {fmtDate(p.expiryDate)}</div> */}
                  <div><span className="text-gray-500">Shelf Life (Days):</span> {p.shelfLifeDays ?? "—"}</div>
                  {/* <div><span className="text-gray-500">Location:</span> {p.location ?? "—"}</div> */}
                  {/* <div><span className="text-gray-500">Created:</span> {fmtDateTime(p.createdAt)}</div>
                  <div><span className="text-gray-500">Updated:</span> {fmtDateTime(p.updatedAt)}</div> */}
                </div>
              </Section>
            </>
          )}
        </div>

        {!loading && (
          <div className="shrink-0 px-6 py-4 border-t border-[#EEF1F5] flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-[#D0D5DD] bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default ProductDetailsModal;

